<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import type { UploadFile, UploadInstance } from 'element-plus'
import { useDevice } from '@/stores/device.store'
import { UploadOutlinedIcon } from '@xpos/icons-svg'
import { useI18n } from 'vue-i18n'
import { notify } from '@/utils/notification'
import ImageCropperDialog from '@/components/ImageCropperDialog.vue'
import ImageDeleteButton from '@/components/image/ImageDeleteButton.vue'
import { fileToDataUrl } from '@/utils/misc'
import { compressImage, uploadImage } from '@/composables/useImageUpload'

const props = withDefaults(defineProps<{
  modelValue?: string[]
  rootFormData?: any
  enableCrop?: boolean
  aspectRatio?: number
  maxSizeMB?: number
  maxWidthOrHeight?: number
  cdsImageType?: 'fullscreen' | 'halfscreen'
}>(), {
  enableCrop: false,
  maxSizeMB: 0.5,
  maxWidthOrHeight: 1280,
})

const emit = defineEmits(['change', 'update:modelValue'])
const { t } = useI18n()
const uploadRef = ref<UploadInstance>()
const maxCount = 10
const showCropper = ref(false)
const cropImageSrc = ref('')
const cropperRef = ref<InstanceType<typeof ImageCropperDialog>>()
const cdsAspectRatio = ref<number | undefined>(undefined)

const computedAspectRatio = computed(() => cdsAspectRatio.value ?? props.aspectRatio)

onMounted(async () => {
  if (!props.cdsImageType) return
  try {
    const deviceStore = useDevice()
    const cdsDevice = await deviceStore.getCdsDevice()
    const fullDevice = await deviceStore.getDeviceById(cdsDevice.uid)
    const key = props.cdsImageType === 'fullscreen' ? 'cds_full_screen_ratio' : 'cds_half_screen_ratio'
    cdsAspectRatio.value = fullDevice?.etc?.[key] as number | undefined
  } catch {}
})

// Compresses and uploads image, then appends to model
const uploadBlob = async (blob: Blob) => {
  const compressed = await compressImage(blob, { maxSizeMB: props.maxSizeMB, maxWidthOrHeight: props.maxWidthOrHeight })
  const res = await uploadImage(compressed)
  emit('update:modelValue', [...(props.modelValue || []), res.file_url])
}
// Handles single file upload per on-change event, clears fileList to prevent accumulation
const handleUpload = async (file: UploadFile) => {
  const currentCount = props.modelValue?.length || 0
  if (currentCount >= maxCount) {
    notify.warning(t('components.FormWidget.UploadWidget.max_images_reached'))
    uploadRef.value?.clearFiles()
    return
  }
  if (!file.raw) { uploadRef.value?.clearFiles(); return }
  if (props.enableCrop) {
    cropImageSrc.value = await fileToDataUrl(file.raw)
    showCropper.value = true
    uploadRef.value?.clearFiles()
    return
  }
  try {
    await uploadBlob(file.raw)
  } catch (error) {
    console.error('Upload failed:', error)
    notify.error('Upload failed')
  } finally {
    uploadRef.value?.clearFiles()
  }
}

const handleCropConfirm = async (blob: Blob) => {
  try {
    await uploadBlob(blob)
    cropperRef.value?.close()
  } catch (error) {
    notify.error('Upload failed')
    cropperRef.value?.onError()
  }
}

const handleDelete = (index: number) => {
  if (!props.modelValue) return
  const newUrls = [...props.modelValue]
  newUrls.splice(index, 1)
  emit('update:modelValue', newUrls)
  uploadRef.value?.clearFiles()
}

const containerStyle = computed(() => {
  const length = props.modelValue?.length || 0
  let height = '160px'
  if (length > 7 && length < 14) height = '260px'
  return { minHeight: height }
})
</script>

<template>
  <div class="multiple-upload-container" :style="containerStyle">
    <el-upload ref="uploadRef" class="avatar-uploader" :show-file-list="false" :on-change="handleUpload" :auto-upload="false"
      :multiple="true" :limit="maxCount" accept="image/*">
      <el-button type="primary" class="shrink-0 w-[50px]">
        <el-icon size="20">
          <UploadOutlinedIcon />
        </el-icon>
      </el-button>
    </el-upload>
    <div class="images-wrap">
      <template v-if="modelValue && modelValue.length">
        <div v-for="(url, index) in modelValue" :key="index" class="image-item">
          <img class="w-[90px] h-[90px] object-contain" :src="url" />
          <ImageDeleteButton @click="handleDelete(index)" />
        </div>
      </template>
    </div>
    <ImageCropperDialog ref="cropperRef" v-model="showCropper" :image-src="cropImageSrc"
      :aspect-ratio="computedAspectRatio" @confirm="handleCropConfirm" />
  </div>
</template>

<style lang="scss" scoped>
.multiple-upload-container {
  .images-wrap {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    position: absolute;
    left: 0;
    top: 50px;
    padding: 10px;
  }
  .image-item {
    position: relative;
    display: inline-block;
    width: 90px;
    height: 90px;
    border-radius: 8px;
    background-color: #fff;
    overflow: hidden;
  }
}
</style>
